<template>
  <div class="c-list-user-permissions-card__container">
    <BittsAvatarPip
      class="mr-8"
      :show-initials="true"
      :org="org || undefined"
      :user="user || undefined"
    />
    <div class="flex flex-col">
      <p class="text-neutral-text-strong font-bold mb-4">
        {{ user?.first_name }} {{ user?.last_name }}
      </p>
      <p class="text-neutral-text text-sm">
        {{ user?.email }}
      </p>
    </div>
    <component
      :is="isDropdownOrText"
      v-bind="bittsDropdownProps"
      class="self-center text-neutral-text mr-18 ml-auto"
      data-testid="user-permission-dropdown"
      @selection="onPermissionSelected"
    >
      <div v-if="!loading">
        {{ capitalizedUserPermission }}
      </div>
      <div v-else>
        Removing
        <span class="dot1">.</span>
        <span class="dot2">.</span>
        <span class="dot3">.</span>
      </div>
    </component>
  </div>
</template>
<script setup lang="ts">
import { BittsAvatarPip, BittsDropdown } from '@crossbeam/bitts';
import { EmptyObject, Nullable } from '@crossbeam/types';

import { capitalize } from 'lodash';
import { computed } from 'vue';

import { EDITOR, OWNER } from '@/constants/shared_list';
import { Partner } from '@/types/partners';
import { Organization, User } from '@/types/root';
import { SharedListOrg, SharedListUser } from '@/types/shared_lists';

type PermissionUser = SharedListUser | User;
type PropUser = PermissionUser & { user_access?: string; user_id?: number };

const {
  editable = false,
  loading = false,
  org = null,
  user = null,
} = defineProps<{
  editable?: boolean;
  loading?: boolean;
  org?: Nullable<SharedListOrg | Partner | Organization | EmptyObject>;
  user?: Nullable<PropUser>;
}>();

const emit = defineEmits<(e: 'option-selected', option: Option) => void>();

export type Option = {
  active?: boolean;
  danger?: boolean;
  display: string;
  value: string;
  user: PropUser;
};

const REMOVE_USER = 'remove_user';

const isDropdownOrText = computed(() =>
  editable && !loading ? BittsDropdown : 'p',
);
const capitalizedUserPermission = computed(() => {
  return capitalize(user?.user_access || OWNER);
});

const bittsDropdownProps = computed(() => {
  const dropdownprops = {
    prompt: capitalizedUserPermission.value,
    placement: 'bottom',
    options: [
      { value: EDITOR, display: 'Editor', user, active: true },
      { value: REMOVE_USER, danger: true, display: 'Remove', user },
    ],
    mountToBody: false,
    parentClass: 'c-list-user-permissions-card__dropdown-container',
  };
  return dropdownprops;
});

function onPermissionSelected(option: Option) {
  if (option.value === REMOVE_USER) {
    emit('option-selected', option);
  }
  // in the future we would want to change someone from an owner to editor or vice versa
  // and can work with that, for now that's not an option so we just want to emit to the parent to remove the user
}
</script>
<style lang="pcss">
@reference "../../css/theme.css";
.c-list-user-permissions-card__container {
  @apply bg-neutral-background-weak flex p-8 rounded-8 mb-8;

  .c-list-user-permissions-card__dropdown-container {
    @apply pt-0;
  }

  .c-bitts-dropdown .ant-popover-open .c-bitts-dropdown__prompt--basic {
    @apply border-neutral-border-focus;
    border-width: 2px !important;
  }
  .c-bitts-dropdown
    .c-bitts-dropdown__prompt
    .c-bitts-dropdown__prompt--basic
    svg {
    @apply text-neutral-accent;
  }
}
</style>
<style scoped lang="pcss">
@reference "../../css/theme.css";
.dot1 {
  animation: visibility 3s linear infinite;
}
@keyframes visibility {
  0% {
    opacity: 1;
  }
  65% {
    opacity: 1;
  }
  66% {
    opacity: 0;
  }
  100% {
    opacity: 0;
  }
}

.dot2 {
  animation: visibility2 3s linear infinite;
}
@keyframes visibility2 {
  0% {
    opacity: 0;
  }
  21% {
    opacity: 0;
  }
  22% {
    opacity: 1;
  }
  65% {
    opacity: 1;
  }
  66% {
    opacity: 0;
  }
  100% {
    opacity: 0;
  }
}

.dot3 {
  animation: visibility3 3s linear infinite;
}
@keyframes visibility3 {
  0% {
    opacity: 0;
  }
  43% {
    opacity: 0;
  }
  44% {
    opacity: 1;
  }
  65% {
    opacity: 1;
  }
  66% {
    opacity: 0;
  }
  100% {
    opacity: 0;
  }
}
</style>
